import { memo, useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { Autocomplete } from 'mui-rff';
import { createFilterOptions } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const REASON_CLEAR = 'clear';
export const REASON_SELECT_OPTION = 'selectOption';
export const REASON_REMOVE_OPTION = 'removeOption';

function MultiSelect({
  label,
  selectAllLabel,
  items,
  disableSelectAll,
  multiple,
  onChange,
  optionDisplayKey,
  optionValueKey,
  customOptionLabel,
  customFilterStringify,
  value,
  ...rest
}) {
  // single
  const [selectedOption, setSelectedOption] = useState(null);
  const handleToggleSingleOption = (selected) => setSelectedOption(selected);
  const handleClearOption = () => setSelectedOption(null);

  // multiple
  const [selectedOptions, setSelectedOptions] = useState([]);
  const allSelected = multiple
    ? items.length === selectedOptions.length
    : false;
  const handleToggleMultipleOption = (selected) => setSelectedOptions(selected);
  const handleClearOptions = () => setSelectedOptions([]);
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOptions(items);
    } else {
      handleClearOptions();
    }
  };
  const handleToggleSelectAll = () => handleSelectAll(!allSelected);
  const handleChange = (event, newValue, reason) => {
    // Toggle selection
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (multiple) {
        // Select all
        if (
          newValue.find((option) => option[optionDisplayKey] === selectAllLabel)
        ) {
          handleToggleSelectAll();

          const result = items.filter(
            (el) => el[optionDisplayKey] !== selectAllLabel,
          );

          handleToggleMultipleOption(result);

          return onChange(result, reason);
        }
        // Select single option
        handleToggleMultipleOption(newValue);
      } else {
        handleToggleSingleOption(newValue);
      }
    }
    // Clear selection
    else if (reason === 'clear') {
      handleClearOptions();
      handleClearOption();
    }

    return onChange(newValue, reason);
  };

  const handleFilterOptions = useCallback(
    (options, params) => {
      const filterOptions = customFilterStringify
        ? { stringify: customFilterStringify }
        : {};
      const filter = createFilterOptions(filterOptions);

      const filtered = filter(options, params);
      const selectAllOption = { id: 0, [optionDisplayKey]: selectAllLabel };

      return multiple && !disableSelectAll
        ? [selectAllOption, ...filtered]
        : [...filtered];
    },
    [
      multiple,
      selectAllLabel,
      customFilterStringify,
      optionDisplayKey,
      disableSelectAll,
    ],
  );

  useEffect(() => {
    if (multiple) return setSelectedOptions(value || []);
    return setSelectedOption(value || null);
  }, [value, multiple]);

  return (
    <Autocomplete
      label={label}
      disableCloseOnSelect={multiple}
      multiple={multiple}
      options={items}
      value={multiple ? selectedOptions : selectedOption}
      getOptionLabel={(option) => option[optionDisplayKey] || option}
      getOptionValue={(option) =>
        optionValueKey ? option[optionValueKey] : option
      }
      filterOptions={handleFilterOptions}
      renderOption={(props, option, { selected }) => {
        const isOptionSelectAll = option[optionDisplayKey] === selectAllLabel;
        const selectAllProps =
          multiple && isOptionSelectAll ? { checked: allSelected } : {};
        let optionLabel = option[optionDisplayKey];

        if (customOptionLabel && !isOptionSelectAll) {
          optionLabel = customOptionLabel.call(this, option);
        }

        return (
          <li
            key={optionLabel}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}>
            <Checkbox
              name="checkboxes"
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...selectAllProps}
            />
            {optionLabel}
          </li>
        );
      }}
      onChange={handleChange}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
}

MultiSelect.defaultProps = {
  items: [],
  selectAllLabel: 'Select all',
  label: '',
  disableSelectAll: false,
  multiple: true,
  onChange: () => {},
  optionDisplayKey: 'name',
};

export default memo(MultiSelect);
